import { useQuery } from '@tanstack/react-query';

import { COUNTRY_COOKIE } from '@/app/consts/cookies';
import {
  EXTENDED_WARRANTY_KEYS,
  MEMBERSHIP_1YR_KEY,
  MEMBERSHIP_KEYS,
} from '@/app/consts/productKeys';
import { graphql, type ResultOf, type VariablesOf } from '@/graphql';
import { getCookie } from '@/utils/cookie';
import gqlClient from '@/utils/gqlClient';

import useCurrency from '../app/hooks/useCurrency';
import { getCartIdCookie } from '../utils/cartUtils';
import productFragment from './fragments/ProductFragment';
import mapProductFragmentToListProduct from './utils/mapProductFragmentToListProduct';

const query = graphql(
  `
    query Products(
      $cartId: String
      $countryCode: String
      $currency: String
      $flags: [String]
      $includeInventory: Boolean
      $productKeys: [String!]
      $productType: String
      $region: String
    ) {
      content(
        currency: $currency
        enabledFeatureFlags: $flags
        region: $region
      ) {
        listProducts(productKeys: $productKeys, productType: $productType) {
          ...ProductFragment
        }
      }
    }
  `,
  [productFragment],
);

export type ProductsResult = ResultOf<typeof query>;
export type ProductsVariables = VariablesOf<typeof query>;

export const DEFAULT_VARS: ProductsVariables = {
  cartId: null,
  countryCode: null,
  currency: null,
  flags: [],
  includeInventory: false,
  productKeys: null,
  productType: null,
  region: null,
};

export const PRODUCTS_ALL_KEY = 'All';

export function mapProductsData(data: ProductsResult) {
  // Reshape results to match the legacy data structure.
  const products = data.content?.listProducts ?? [];
  return products
    .filter((product) => product !== null)
    .map(mapProductFragmentToListProduct);
}

export function withQueryKey(vars: ProductsVariables) {
  const cartId = getCartIdCookie();

  // Enforce that we always have consitent cache keys that match the graphql query
  const queryVars: ProductsVariables = {
    ...DEFAULT_VARS,
    ...vars,
    cartId,
  };

  // Include country from cookies if none was provided to ensure we're getting correct inventory for user's country
  if (queryVars.includeInventory && !queryVars.countryCode) {
    const countryCookie = getCookie(COUNTRY_COOKIE);
    queryVars.countryCode = countryCookie;
  }

  return {
    queryKey: [
      'useProductsData',
      queryVars.productType ?? PRODUCTS_ALL_KEY, // Use "All" key when no productType
      { vars: queryVars },
    ],
    queryFn: async () => {
      const data = await gqlClient().request(query, queryVars);
      return mapProductsData(data);
    },
  };
}

export function useMembershipProduct() {
  const currency = useCurrency();
  return useQuery({
    ...withQueryKey({
      currency: currency.currencyCode,
      productKeys: MEMBERSHIP_KEYS,
    }),
    select: (data) => {
      return {
        annual: data.find(
          (product) => product.handle === 'one-year-membership',
        ),
        monthly: data.find((product) => product.handle === 'subscription'),
      };
    },
  });
}

export function useAddOnBundleData() {
  const currency = useCurrency();
  return useQuery({
    ...withQueryKey({
      currency: currency.currencyCode,
      productKeys: [MEMBERSHIP_1YR_KEY, ...EXTENDED_WARRANTY_KEYS],
    }),
    select: (data) => {
      return {
        oneYearMembership: data.find(
          (product) => product.handle === 'one-year-membership',
        ),
        twoYearWarranty: data.find(
          (product) => product.handle === 'extended-warranty-2year',
        ),
        threeYearWarranty: data.find(
          (product) => product.handle === 'extended-warranty-3year',
        ),
      };
    },
  });
}

export function useProductsData({
  includeInventory = false,
  productKeys = null,
}: {
  includeInventory?: boolean;
  productKeys?: string[] | null;
}) {
  const currency = useCurrency();

  return useQuery({
    ...withQueryKey({
      currency: currency.currencyCode,
      includeInventory,
      productKeys,
    }),
  });
}
